/**
 * Stripe Configuration for HajiPay
 * White-label payment processing configuration
 * Follows PCI compliance and Islamic finance standards
 */

import { Appearance } from '@stripe/stripe-js';

export const STRIPE_CONFIG = {
  // API Configuration
  apiVersion: '2024-06-20',

  // Publishable Key (loaded from environment)
  publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY!,

  // Base URL for success/return URLs
  appBaseUrl: import.meta.env.VITE_APP_BASE_URL || 'http://localhost:5174',

  // White-label Appearance Configuration (HajiPay Branding)
  appearance: {
    theme: 'stripe',
    variables: {
      // Brand Colors - Emerald/Teal theme for Islamic finance
      colorPrimary: '#059669', // emerald-600
      colorBackground: '#ffffff',
      colorText: '#30313d',
      colorDanger: '#df1b41',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      spacingUnit: '2px',
      borderRadius: '6px',
    },
    rules: {
      '.Input': {
        boxShadow: '0 0 0 1px #d1d5db',
        border: '1px solid #d1d5db',
      },
      '.Input:focus': {
        boxShadow: '0 0 0 2px #059669',
        border: '1px solid #059669',
      },
      '.Label': {
        color: '#374151',
        fontWeight: '500',
      },
      '.Error': {
        color: '#df1b41',
      },
    },
  } as Appearance,

  // Payment Element Configuration
  paymentElementOptions: {
    layout: {
      type: 'tabs' as const,
      defaultCollapsed: false,
    },
    paymentMethodOrder: ['card', 'link'],
    fields: {
      billingDetails: {
        name: 'auto',
        email: 'auto',
        address: {
          country: 'auto',
        },
      },
    },
  },

  // Setup Intent Configuration (for subscriptions)
  setupIntentOptions: {
    payment_method_types: ['card'],
    usage: 'off_session' as const,
  },

  // Webhook Configuration
  webhookEvents: [
    'payment_intent.succeeded',
    'payment_intent.payment_failed',
    'payment_intent.canceled',
    'charge.dispute.created',
    'invoice.payment_succeeded',
    'invoice.payment_failed',
    'customer.subscription.created',
    'customer.subscription.updated',
    'customer.subscription.deleted',
  ],

  // Currency Configuration
  supportedCurrencies: ['usd', 'eur', 'gbp'],

  // Amount Limits (in cents)
  limits: {
    minAmount: 100, // $1.00
    maxAmount: 1000000, // $10,000.00
    maxSubscriptionAmount: 500000, // $5,000.00 for subscriptions
  },

  // Retry Configuration
  retry: {
    maxAttempts: 3,
    backoffMultiplier: 2,
    initialDelay: 1000, // 1 second
  },

  // Timeout Configuration
  timeouts: {
    paymentIntent: 30000, // 30 seconds
    setupIntent: 30000, // 30 seconds
    webhook: 10000, // 10 seconds
  },
};

/**
 * Validate Stripe configuration
 */
export const validateStripeConfig = (): void => {
  if (!STRIPE_CONFIG.publishableKey) {
    throw new Error('VITE_STRIPE_PUBLISHABLE_KEY is required');
  }

  if (!STRIPE_CONFIG.appBaseUrl) {
    throw new Error('VITE_APP_BASE_URL is required');
  }

  // Validate publishable key format
  if (!STRIPE_CONFIG.publishableKey.startsWith('pk_')) {
    throw new Error('Invalid Stripe publishable key format');
  }
};

/**
 * Get environment-specific configuration
 */
export const getStripeEnvironment = (): 'development' | 'production' => {
  return STRIPE_CONFIG.publishableKey.startsWith('pk_live_') ? 'production' : 'development';
};

/**
 * Check if we're in production
 */
export const isProduction = (): boolean => getStripeEnvironment() === 'production';

/**
 * Get currency configuration
 */
export const getCurrencyConfig = (currency: string) => {
  const config = {
    usd: { symbol: '$', decimals: 2, minAmount: 100 },
    eur: { symbol: '€', decimals: 2, minAmount: 100 },
    gbp: { symbol: '£', decimals: 2, minAmount: 100 },
  };

  return config[currency as keyof typeof config] || config.usd;
};

/**
 * Format amount for Stripe
 * Expects amount already in smallest currency unit (cents for USD).
 * Callers must convert dollars to cents before calling.
 * Returns the integer amount, rounding any floating-point artifacts.
 */
export const formatAmountForStripe = (amount: number, _currency: string): number => {
  return Math.round(amount);
};

/**
 * Format amount in cents for display.
 * Delegates to the centralized currency module for consistent formatting.
 */
export const formatAmountForDisplay = (amount: number, currency: string): string => {
  // Inline implementation kept to avoid circular dependency with @lib/currency
  // during Stripe provider initialization. Logic mirrors formatCents().
  const currencyConfig = getCurrencyConfig(currency);
  const displayAmount = amount / Math.pow(10, currencyConfig.decimals);

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(displayAmount);
};

/**
 * Validate donation amount
 */
export const validateDonationAmount = (amount: number, currency: string): { isValid: boolean; error?: string } => {
  const currencyConfig = getCurrencyConfig(currency);
  const stripeAmount = formatAmountForStripe(amount, currency);

  if (stripeAmount < currencyConfig.minAmount) {
    return {
      isValid: false,
      error: `Minimum donation amount is ${formatAmountForDisplay(currencyConfig.minAmount, currency)}`,
    };
  }

  if (stripeAmount > STRIPE_CONFIG.limits.maxAmount) {
    return {
      isValid: false,
      error: `Maximum donation amount is ${formatAmountForDisplay(STRIPE_CONFIG.limits.maxAmount, currency)}`,
    };
  }

  return { isValid: true };
};

/**
 * Validate subscription amount
 */
export const validateSubscriptionAmount = (amount: number, currency: string): { isValid: boolean; error?: string } => {
  const currencyConfig = getCurrencyConfig(currency);
  const stripeAmount = formatAmountForStripe(amount, currency);

  if (stripeAmount < currencyConfig.minAmount) {
    return {
      isValid: false,
      error: `Minimum subscription amount is ${formatAmountForDisplay(currencyConfig.minAmount, currency)}`,
    };
  }

  if (stripeAmount > STRIPE_CONFIG.limits.maxSubscriptionAmount) {
    return {
      isValid: false,
      error: `Maximum subscription amount is ${formatAmountForDisplay(STRIPE_CONFIG.limits.maxSubscriptionAmount, currency)}`,
    };
  }

  return { isValid: true };
};
